import { ChangeDetectionStrategy, Component, Input, OnChanges, ViewChild } from "@angular/core";
import { Vision } from "@common/ADAPT.Common.Model/organisation/vision";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { IAdaptMenuItem, MenuComponent } from "@common/ux/menu/menu.component";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { DxAccordionComponent } from "devextreme-angular";
import { BehaviorSubject, filter, forkJoin, lastValueFrom, map, switchMap, tap } from "rxjs";
import { CategoryVisionAccordionComponent } from "../category-vision-accordion/category-vision-accordion.component";
import { VisionService } from "../vision.service";
import { VisionAuthService } from "../vision-auth.service";

@Component({
    selector: "adapt-display-visions-by-person",
    standalone: false,
    templateUrl: "./display-visions-by-person.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["../adapt-vision-common.scss"],
})
export class DisplayVisionsByPersonComponent extends BaseComponent implements OnChanges {
    @Input() public teamId?: number;
    public teamMembers: Person[] = [];
    public personCompletionStatus: { [personId: number]: boolean } = {};
    public editIntegratedVisionMenuItem: IAdaptMenuItem = {
        text: "Edit integrated vision",
        icon: "fal fa-fw fa-edit",
        separatorTop: true,
        onClick: () => this.editIntegratedVision(),
    };
    public assignIntegratedVisionMenuItem: IAdaptMenuItem = {
        text: "Assign integrated vision",
        icon: "fal fa-fw fa-layer-plus",
        onClick: () => this.assignIntegratedVision(),
    };
    public recaptureVisionMenuItem: IAdaptMenuItem = {
        text: "Recapture vision",
        icon: "fal fa-fw fa-arrows-retweet",
        separatorTop: true,
        onClick: () => this.visionService.openCaptureVisionDialog(this.teamId!).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(),
    };
    public menuItems = [{
        text: "",
        icon: MenuComponent.SmallRootMenu.icon,
        items: [
            {
                text: "Expand all people",
                icon: "fal fa-fw fa-chevrons-down",
                onClick: () => this.expandAll(),
            },
            {
                text: "Collapse all people",
                icon: "fal fa-fw fa-chevrons-up",
                onClick: () => this.collapseAll(),
            },
            this.editIntegratedVisionMenuItem,
            this.assignIntegratedVisionMenuItem,
            this.recaptureVisionMenuItem,
        ],
    }];

    public expandedPersonIds: number[] = [];
    public canEditTeamVision = false;

    private personAccordion: { [personId: number]: CategoryVisionAccordionComponent } = {};
    private updateStatus$ = new BehaviorSubject<void>(undefined);
    @ViewChild(DxAccordionComponent) private accordion?: DxAccordionComponent;

    public constructor(
        private teamsService: CommonTeamsService,
        private visionService: VisionService,
        private visionAuthService: VisionAuthService,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super();

        rxjsBreezeService.entityTypeChanged(Vision).pipe(
            filter((vision) => !!this.teamId && vision.teamId === this.teamId),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.updateStatus$.next());
    }

    public async ngOnChanges() {
        if (this.teamId) {
            this.canEditTeamVision = await lastValueFrom(this.visionAuthService.canEditVisionForTeamId(this.teamId));
            this.editIntegratedVisionMenuItem.visible = this.canEditTeamVision;
            this.assignIntegratedVisionMenuItem.visible = this.canEditTeamVision;
            this.recaptureVisionMenuItem.visible = this.canEditTeamVision;

            const team = await lastValueFrom(this.teamsService.getTeamById(this.teamId));
            this.updateStatus$.pipe(
                switchMap(() => this.teamsService.promiseToGetActiveTeamMembers(team!)),
                // this.teamMembers is needed as dataSource to the accordion
                tap((members) => {
                    this.teamMembers = members.sort((a, b) => a.fullName.localeCompare(b.fullName));
                    if (members.length === 0) {
                        this.isInitialised = true;
                    }
                }),
                filter((members) => members.length > 0),
                switchMap(() => forkJoin(this.teamMembers.map((member) => this.visionService.getVisionsByPersonTeam(member.personId, this.teamId!).pipe(
                    map((visions) => ({ personId: member.personId, isCompleted: this.visionService.areAllVisionsCompleted(visions), count: visions.length })),
                )))),
                this.takeUntilDestroyed(),
            ).subscribe((personVisionsCompletion) => {
                this.personCompletionStatus = {};
                for (const status of personVisionsCompletion) {
                    this.personCompletionStatus[status.personId] = status.isCompleted;
                    if (status.count === 0) {
                        this.teamMembers = this.teamMembers.filter((m) => m.personId !== status.personId);
                    }
                }

                this.isInitialised = true;
            });
        }
    }

    public onAccordionExpansionChanged(personIds: number[]) {
        this.expandedPersonIds = personIds;
    }

    public editVisionForPerson(personId: number) {
        if (this.teamId && personId) {
            return this.visionService.editVisionForPerson(personId, this.teamId, undefined, "Return to team vision page").subscribe();
        }
    }

    public expandAll() {
        if (this.accordion?.instance && this.teamMembers.length) {
            this.teamMembers.forEach((_, index) => this.accordion?.instance.expandItem(index));
        }
    }

    public collapseAll() {
        if (this.accordion?.instance && this.teamMembers.length) {
            this.teamMembers.forEach((_, index) => this.accordion?.instance.collapseItem(index));
        }
    }

    public indexOf(person: Person) {
        return this.teamMembers.findIndex((m) => m.personId === person.personId);
    }

    public personAccordionInitialised(personId: number, accordion: CategoryVisionAccordionComponent) {
        this.personAccordion[personId] = accordion;
    }

    public expandPerson(personId: number) {
        this.personAccordion[personId]?.expandCategories();
    }

    public collapsePerson(personId: number) {
        this.personAccordion[personId]?.collapseCategories();
    }

    private editIntegratedVision() {
        if (this.teamId) {
            return this.visionService.editIntegratedVision(this.teamId, undefined, "Return to team vision page", true).subscribe();
        }
    }

    private assignIntegratedVision() {
        this.visionService.assignIntegratedVision(this.teamId!).pipe(
            this.takeUntilDestroyed(),
        ).subscribe();
    }
}
